import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const COL_CUR = "#EC4899";  // pink
const COL_LAST = "#1E3A8A"; // dark blue

const T = ({ active, payload, label, unit = "", digits = 2 }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E5E5DE] p-3 font-mono-data text-xs shadow-sm">
      <div className="text-[#1A1B1A] font-semibold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex justify-between gap-6" style={{ color: p.color }}>
          <span>{p.name}</span>
          <span>
            {typeof p.value === "number" ? p.value.toFixed(digits) : p.value}
            {unit}
          </span>
        </div>
      ))}
    </div>
  );
};

function YoYPanel({ title, testid, data, xKey, curKey, lastKey, showLast, unit = "", digits = 2, tickFmt, note }) {
  return (
    <div data-testid={testid} className="flat-card p-6 sm:p-8">
      <div className="overline mb-2">Year-over-year</div>
      <h3 className="font-display text-xl tracking-tight mb-1">{title}</h3>
      {note && <div className="text-[10px] font-mono-data uppercase tracking-[0.24em] text-[#6E716C] mb-3">{note}</div>}
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#EDEDE8" vertical={false} />
          <XAxis dataKey={xKey} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
          <YAxis tickLine={false} axisLine={false} tickFormatter={tickFmt} width={50} />
          <Tooltip content={<T unit={unit} digits={digits} />} />
          <Legend iconType="line" wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
          <Line type="monotone" dataKey={curKey} name="Current Year" stroke={COL_CUR} strokeWidth={2} dot={{ r: 3 }} />
          {showLast && (
            <Line type="monotone" dataKey={lastKey} name="Last Year" stroke={COL_LAST} strokeWidth={2} dot={{ r: 3 }} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function YoYCharts({ series, yoy, months }) {
  const hasPaired = Array.isArray(series) && series.length > 0;
  const monthsTracked = yoy?.months_tracked ?? (months?.length ?? 0);
  const monthsLeft = Math.max(0, 12 - monthsTracked);

  // Prefer the paired YoY series when available; otherwise fall back to raw months (single-line)
  const rows = hasPaired
    ? series
    : (months || []).map((m) => ({
        x: m.month_label,
        kwh_current: m.kwh,
        cost_current: m.cost_rm,
        load_factor_current: m.load_factor,
        power_factor_current: m.power_factor,
      }));

  const xKey = hasPaired ? "month_label" : "x";
  const hasPFCur = rows.some((r) => (r.power_factor_current || 0) > 0);
  const hasPFLast = hasPaired && rows.some((r) => (r.power_factor_last || 0) > 0);

  const note = hasPaired
    ? null
    : monthsLeft > 0
      ? `${monthsLeft} more month${monthsLeft === 1 ? "" : "s"} to unlock last-year overlay`
      : null;

  if (!rows.length) {
    return (
      <section data-testid="yoy-charts" className="space-y-4">
        <div className="flex items-baseline justify-between border-b border-[#E5E5DE] pb-3">
          <div>
            <div className="overline mb-1">Section</div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight">Year-over-Year Trends</h2>
          </div>
        </div>
        <div className="flat-card p-8 text-center font-mono-data text-sm text-[#6E716C]">
          Upload some bills to start seeing yearly trends.
        </div>
      </section>
    );
  }

  return (
    <section data-testid="yoy-charts" className="space-y-4">
      <div className="flex items-baseline justify-between border-b border-[#E5E5DE] pb-3">
        <div>
          <div className="overline mb-1">Section</div>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight">Year-over-Year Trends</h2>
        </div>
        <div className="text-xs font-mono-data text-[#6E716C]">
          {hasPaired
            ? `${rows.length} paired month${rows.length === 1 ? "" : "s"}`
            : `${monthsTracked} / 12 months`}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <YoYPanel
          testid="yoy-chart-lf"
          title="Year-to-year Load Factors Trend"
          data={rows}
          xKey={xKey}
          curKey="load_factor_current"
          lastKey="load_factor_last"
          showLast={hasPaired}
          digits={3}
          tickFmt={(v) => `${(v * 100).toFixed(0)}%`}
          note={note}
        />
        <YoYPanel
          testid="yoy-chart-pf"
          title="Year-to-year Power Factors Trend"
          data={rows}
          xKey={xKey}
          curKey="power_factor_current"
          lastKey="power_factor_last"
          showLast={hasPaired && hasPFLast}
          digits={3}
          tickFmt={(v) => `${(v * 100).toFixed(0)}%`}
          note={hasPFCur ? note : "No power factor data on record yet"}
        />
        <YoYPanel
          testid="yoy-chart-bill"
          title="Year-to-year Bill Amount Trend"
          data={rows}
          xKey={xKey}
          curKey="cost_current"
          lastKey="cost_last"
          showLast={hasPaired}
          unit=" RM"
          digits={2}
          tickFmt={(v) => v.toLocaleString()}
          note={note}
        />
        <YoYPanel
          testid="yoy-chart-kwh"
          title="Year-to-year Energy Consumption Trend"
          data={rows}
          xKey={xKey}
          curKey="kwh_current"
          lastKey="kwh_last"
          showLast={hasPaired}
          unit=" kWh"
          digits={0}
          tickFmt={(v) => v.toLocaleString()}
          note={note}
        />
      </div>
    </section>
  );
}
